// TERMS[metier] — Source UNIQUE de vérité du VOCABULAIRE métier côté front.
//
// Principe (décision Lucas, juin 2026) : « un seul moteur, trois histoires ».
// Le moteur transactionnel est unique ; l'EXPÉRIENCE (les mots) doit être
// radicalement différente selon le métier. Aucune chaîne visible par un
// utilisateur ne doit être écrite « en dur » dans un composant React : tout
// passe par ce module, pour qu'il devienne IMPOSSIBLE qu'un « Article vendu »
// apparaisse dans une location ou qu'un texte de vente surgisse dans un service.
//
// ⚠️ On ne réinvente PAS la détection du métier : on réutilise les helpers
// canoniques de ``rental.js`` (eux-mêmes synchronisés avec le backend
// ``listing_guards.py`` → KNOWN_LISTING_TYPES). Clé = ``listing_type`` ∈
// {sale, rental, service, free}. Tout type absent / inconnu ⇒ « sale » (legacy
// forward-safe : les annonces anciennes sans ``listing_type`` restent des ventes).
//
// Vocabulaire de référence (Lucas, 2026-06-22) :
//   VENTE    : vendeur · acheteur · article · acheter · expédier · recevoir
//   LOCATION : loueur · locataire · bien · réserver · récupérer · restituer
//   SERVICE  : prestataire · client · prestation · prendre rdv · intervenir · terminer

import { isRentalListing, isServiceListing } from "./rental";

/** Normalise une annonce OU un type-string vers un métier connu de TERMS. */
const metierOf = (listingOrType) => {
  if (typeof listingOrType === "string") {
    return ["sale", "rental", "service"].includes(listingOrType)
      ? listingOrType
      : "sale"; // free / inconnu / null ⇒ vocabulaire vente par défaut
  }
  if (isServiceListing(listingOrType)) return "service";
  if (isRentalListing(listingOrType)) return "rental";
  return "sale";
};

// ── VENTE ───────────────────────────────────────────────────────────────────
// PARITÉ STRICTE avec l'existant : ces chaînes reproduisent EXACTEMENT les
// textes actuels du checkout afin de garantir zéro régression visuelle sur le
// flux massif (la vente). Ne pas reformuler sans intention.
const SALE = {
  metier: "sale",
  actor: "vendeur", // l'offreur
  actorRecap: "Vendeur", // label récap (majuscule)
  buyer: "acheteur",
  object: "article",
  objectDemo: "cet article",
  objectDef: "l'article", // forme avec article défini (« l'article se libère »)
  priceLabel: "Prix article",
  // Récapitulatif — prix issu d'une offre acceptée (06/10/2026 : décliné par
  // métier ; « offre » n'a de sens que pour la vente).
  agreedLabel: "Prix convenu via votre offre acceptée",
  // Étape 1 — phrase d'identification (mode main propre)
  identifyLine:
    "Ces informations permettent au vendeur de vous identifier et de vous contacter si besoin.",
  // Étape 2 — vérification en présence (main propre)
  verifyHeading: "Vérification — main propre",
  /* ─── CORRIGÉ LE 16/09/2026 — LE TEXTE DISAIT LE CONTRAIRE DU CODE ───────
     Lucas : « l'utilisation de la plateforme ne dois pas faire peur !!! » — et
     ici ce n'était pas seulement anxiogène, c'était FAUX.

     ANCIEN TEXTE, mot pour mot :
       verifyRest    : « Le paiement déclenche le reversement et n'est pas
                         annulable une fois validé. »
       importantNote : « … Une fois le paiement confirmé, l'argent part vers le
                         vendeur. »

     CE QUE FAIT RÉELLEMENT LE SYSTÈME, vérifié sur pièces :
       · backend/routes/stripe_purchase.py:476 — la transaction naît avec
         `"payout_status": "not_eligible"`. Rien ne part ;
       · backend/services/payout_eligibility.py:89/128 — une vente en main
         propre n'ouvre sa fenêtre de versement qu'APRÈS confirmation de
         l'acheteur, puis 2 h (`PAYOUT_DELAY_HANDOVER_CONFIRMED_HOURS`) si le
         code de remise a été saisi, 48 h en repli de sécurité ;
       · backend/routes/stripe_purchase.py:2759 `buyer_refund_request` — un
         endpoint ACHETEUR qui bascule `payout_status: "blocked"` et gèle tout
         reversement. Il est exposé dans l'UI : « Signaler un problème »
         (frontend/src/components/BuyerDeliveryWidget.js:277).

     DONC : « n'est pas annulable » NIAIT un recours qui existe, et « l'argent
     part vers le vendeur » décrivait une immédiateté qui n'existe pas. L'écran
     SUIVANT disait d'ailleurs l'inverse, et de façon rassurante — « Vos gains
     et le don sont versés après réception confirmée »
     (pages/payment/StripePurchasePage.js:52). L'acheteur lisait la version la
     plus angoissante d'abord, et c'était la fausse.

     CE QUI EST GARDÉ : la consigne protectrice. Ne pas valider à l'aveugle est
     un vrai conseil, à l'instant où une erreur coûte cher.
     CE QUI PART : la double affirmation d'irrévocabilité — dite deux fois de
     suite, en clair puis dans l'encadré ambre, au moment précis où l'acheteur
     hésite. Règle maison appliquée : « le wording produit reflète la réalité ».

     06/10/2026 — UNE SEULE CONSIGNE PAR ÉCRAN (audit des tunnels, Lucas :
     « Dépolu quand il y a trop de texte doublon »). L'étape 2 disait la même
     chose quatre fois : « Avant de payer, vérifiez l'article avec le vendeur »,
     `verifyRest`, l'encadré `importantNote`, puis `rdvQuestion` au-dessus d'un
     bouton qui le répète. `verifyStrong`, `verifyRest` et `rdvQuestion` sont
     retirés ; l'encadré, seul, porte la consigne ET la protection réelle.
     `verifyRest` était d'ailleurs inexact : ce n'est pas le paiement qui
     « conclut la vente », c'est la confirmation de la remise qui libère les
     fonds. */
  /* 06/10/2026 (soir) — Lucas : « trop de texte… faire plus simple tout en
     conservant la sécurité ! Être plus succinct ». La consigne tient en une
     ligne en gras (`consigne`), la protection réelle en une phrase
     (`importantNote`). « encaissé par Stripe » part : la page de paiement et le
     pied du récapitulatif le disent déjà. */
  // 06/10/2026 (soir) — UN SEUL CODE, AVANT LE PAIEMENT (décision de Lucas) :
  // le code de rencontre confirme la remise, il n'y a plus de second code
  // (backend/services/remise_par_rendezvous.py). L'acheteur doit donc savoir
  // que PAYER = CONFIRMER : on ne paie que l'article vérifié, en main.
  consigne: "Ne payez qu'en face du vendeur, l'article vérifié et en main.",
  importantNote: "Votre paiement confirme la remise.",
  presenceQuestion: "Êtes-vous actuellement avec le vendeur ?",
  presenceYes: "Oui, je suis avec le vendeur et l'article me convient",
  presenceNo: "Non, je veux d'abord contacter le vendeur",
  contactInfo:
    "Convenez d'un rendez-vous avec le vendeur, puis revenez payer sur place.",
  contactCta: "Contacter le vendeur →",
  // Avertissement de timing de paiement — AUCUN pour la vente (on paie sur place).
  paymentWarning: null,
  // ── Fiche annonce (ListingDetailPage) ──
  reservedTitle: "Article réservé", // encart « réservé à un autre acheteur »
  reserveAgainVerb: "l'acheter", // « vous pourrez {…} ou faire une offre »
  bookingCtaTitle: "Acheter cet article", // titre du panneau d'action (vente : non utilisé, fallback sûr)
  // — Suivi : cartes « Mes achats » + widget de réception + badge carte annonce —
  purchaseKind: "Achat article",
  objectTitle: "Article", // repli de titre (capitalisé)
  soldBadge: "Vendu", // badge sur la carte d'annonce au statut terminal
  receivedQuestion: "Avez-vous récupéré votre article ?",
  providedByLabel: "vendu par",
  confirmReceiptCta: "✅ Oui, j'ai reçu",
  // Modalité de remise affichée sur la fiche + les cartes. Vente/location =
  // remise physique ; service = rendez-vous (pas de « remise »).
  remiseLabel: "Remise en main propre",
  remiseLabelShort: "Main propre",
  // ── Gate CO-PRÉSENCE — code de rencontre TOURNANT (25/10/2026) ──
  // Le vendeur ouvre « Mon code de rencontre » dans son compte et lit le code
  // (il change toutes les 30 s) à l'acheteur présent. Rien n'est envoyé à distance.
  rdvNotPresent: "Pas encore sur place ?",
  rdvEnterLabel: "Code de rencontre",
  rdvEnterHint: "Demandez au vendeur d'ouvrir « Mon code de rencontre » dans son compte Gively, puis tapez le code affiché.",
  rdvConfirmed: "Code accepté — vous pouvez payer.",
  rdvExpiredMsg: "Les 10 minutes pour payer sont passées. Tapez le code affiché maintenant par le vendeur.",
};

// ── LOCATION ─────────────────────────────────────────────────────────────────
const RENTAL = {
  metier: "rental",
  actor: "loueur",
  actorRecap: "Loueur",
  buyer: "locataire",
  object: "bien",
  objectDemo: "ce bien",
  objectDef: "le bien",
  // 06/10/2026 — « Prix », plus « Tarif » : la ligne porte le TOTAL de la
  // réservation, pas un tarif journalier (audit des tunnels).
  priceLabel: "Prix de la location",
  agreedLabel: "Réservation acceptée par le loueur",
  identifyLine:
    "Ces informations permettent au loueur de vous identifier et de vous contacter si besoin.",
  verifyHeading: "Vérification — début de location",
  /* MÊME CORRECTION QUE LA VENTE (16/09/2026) — et c'est MA PROPRE GARDE qui a
     trouvé ce sibling. Le balayage de surfaces n'avait signalé que le bloc
     VENTE ; la garde `test_le_tunnel_d_achat_ne_promet_plus_une_irrevocabilite`
     lit le fichier ENTIER et a échoué ici. Une garde qui ne regarde que ce
     qu'on lui a montré ne vaut pas mieux que l'audit qui l'a produite.
     Même motif : le versement au loueur passe par le même
     `services/payout_eligibility.py`, et `buyer_refund_request` gèle le
     reversement à la demande du locataire. « Pas annulable » était faux ici
     aussi.
     06/10/2026 — `verifyRest` retiré (inexact : c'est le CODE DE REMISE saisi
     après paiement qui démarre la location). `paymentWarning` s'affiche
     désormais EN TÊTE du même encadré : l'encadré ne répète donc plus « au
     moment du rendez-vous ». */
  importantNote: "Vérifiez le bien sur place : le loueur n'est payé qu'une fois la remise confirmée.",
  presenceQuestion: "Êtes-vous actuellement avec le loueur ?",
  presenceYes: "Oui, je suis avec le loueur et le bien me convient",
  presenceNo: "Non, je veux d'abord contacter le loueur",
  contactInfo:
    "Convenez d'abord d'un rendez-vous avec le loueur.",
  contactCta: "Contacter le loueur →",
  paymentWarning: "Ne payez jamais avant le rendez-vous.",
  reservedTitle: "Bien réservé",
  reserveAgainVerb: "le louer",
  bookingCtaTitle: "Louer ce bien",
  purchaseKind: "Location",
  objectTitle: "Bien",
  soldBadge: "Terminé",
  receivedQuestion: "Avez-vous récupéré le bien loué ?",
  providedByLabel: "loué par",
  confirmReceiptCta: "✅ Oui, je l'ai récupéré",
  remiseLabel: "Remise en main propre",
  remiseLabelShort: "Main propre",
  // Code de rencontre tournant (25/10/2026) — même parcours que la vente.
  rdvNotPresent: "Pas encore sur place ?",
  rdvEnterLabel: "Code de rencontre",
  rdvEnterHint: "Demandez au loueur d'ouvrir « Mon code de rencontre » dans son compte Gively, puis tapez le code affiché.",
  rdvConfirmed: "Code accepté — vous pouvez payer.",
  rdvExpiredMsg: "Les 10 minutes pour payer sont passées. Tapez le code affiché maintenant par le loueur.",
};

// ── SERVICE ──────────────────────────────────────────────────────────────────
const SERVICE = {
  metier: "service",
  actor: "prestataire",
  actorRecap: "Prestataire",
  buyer: "client",
  object: "prestation",
  objectDemo: "cette prestation",
  objectDef: "la prestation",
  priceLabel: "Prix de la prestation",
  agreedLabel: "Rendez-vous et montant convenus avec le prestataire",
  identifyLine:
    "Ces informations permettent au prestataire de vous identifier et de vous contacter si besoin.",
  verifyHeading: "Vérification — rendez-vous",
  /* Troisième occurrence du même défaut, trouvée par la même garde.
     06/10/2026 — `verifyStrong`/`verifyRest` retirés ; `paymentWarning` ouvre
     l'encadré, qui ne répète donc plus « au moment du rendez-vous ». */
  importantNote: "Le prestataire n'est payé qu'une fois la prestation confirmée.",
  presenceQuestion: "Êtes-vous actuellement avec le prestataire ?",
  presenceYes: "Oui, je suis avec le prestataire",
  presenceNo: "Non, je veux d'abord contacter le prestataire",
  contactInfo:
    "Convenez d'abord d'un rendez-vous avec le prestataire.",
  contactCta: "Contacter le prestataire →",
  paymentWarning: "Ne payez qu'au moment du rendez-vous.",
  reservedTitle: "Prestation réservée",
  reserveAgainVerb: "la réserver",
  bookingCtaTitle: "Réserver cette prestation",
  purchaseKind: "Réservation",
  objectTitle: "Prestation",
  soldBadge: "Terminé",
  receivedQuestion: "La prestation a-t-elle été réalisée ?",
  providedByLabel: "réalisée par",
  confirmReceiptCta: "✅ Oui, c'est fait",
  // Un service n'est jamais « remis en main propre » : c'est un rendez-vous.
  remiseLabel: "Rendez-vous sur place ou en visio",
  remiseLabelShort: "Sur place ou visio",
  // Code de rencontre tournant (25/10/2026) — même parcours que la vente.
  rdvNotPresent: "Pas encore sur place ?",
  rdvEnterLabel: "Code de rencontre",
  rdvEnterHint: "Demandez au prestataire d'ouvrir « Mon code de rencontre » dans son compte Gively, puis tapez le code affiché.",
  rdvConfirmed: "Code accepté — vous pouvez payer.",
  rdvExpiredMsg: "Les 10 minutes pour payer sont passées. Tapez le code affiché maintenant par le prestataire.",
};

const _TERMS = { sale: SALE, rental: RENTAL, service: SERVICE };

// Message RASSURANT (décision Lucas, juillet 2026) affiché quand une annonce
// PAYANTE part automatiquement en BROUILLON faute de compte de versement Stripe
// configuré. Générique (tous métiers), jamais anxiogène : l'utilisateur comprend
// immédiatement pourquoi et que rien n'est perdu. Source de vérité unique du copy.
export const DRAFT_GATE = {
  title: "Configurez vos versements pour publier cette annonce",
  body:
    "Votre annonce est sauvegardée en brouillon. Configurez votre compte de "
    + "versement pour la publier en un clic.",
  cta: "Configurer mes versements",
  // Bandeau court « Reprendre ma publication » (retour d'onboarding → /mes-annonces).
  resumeBanner: "Votre brouillon vous attend : finalisez vos versements pour le publier.",
  resumeCta: "Reprendre ma publication",
};

// Titre principal du tunnel de checkout, décliné par métier.
const _CHECKOUT_TITLE = {
  sale: "Acheter cet article",
  rental: "Réserver cette location",
  service: "Réserver cette prestation",
};

/**
 * Retourne le bloc de vocabulaire métier d'une annonce (ou d'un type).
 * @param {object|string} listingOrType  L'objet listing reçu de l'API, ou
 *   directement le ``listing_type``. ``null``/inconnu ⇒ vocabulaire vente.
 */
export const getTerms = (listingOrType) => {
  const m = metierOf(listingOrType);
  const base = _TERMS[m] || SALE;
  return { ...base, title: _CHECKOUT_TITLE[m] || _CHECKOUT_TITLE.sale };
};

/** Mot désignant l'offreur (« vendeur » / « loueur » / « prestataire »). */
export const actorWord = (listingOrType) => getTerms(listingOrType).actor;

/** Mot désignant l'objet de la transaction (« article » / « bien » / « prestation »). */
export const objectWord = (listingOrType) => getTerms(listingOrType).object;

export default getTerms;
